import { Box, IconButton, Popover, Typography } from "@mui/material";
import React, { useState } from "react";
import { DataSourcesStore } from "../stores/DataSourcesStore";
import { observer } from "mobx-react";
import theme from "Theme/AppTheme";
import { v4 as uuidv4 } from "uuid";
import AddButton from "../components/AddButton";
import CampaignDefinition from "./CampaignDefinition";
import MediaSegments from "./MediaSegments";
import DateFormatPattern from "./DateFormatPattern";
import LanguageIndicatorDefinition from "./LanguageIndicatorDefinition";
import Section from "./Section";
import {
    emptyCampaignIndicator,
    emptyLanguageIndicator,
} from "../helpers/emptyStates";
import InfoIcon from "@mui/icons-material/Info";
import { DateFormatHelperText } from "components/Admin/AdminUIComponents/MetadataTemplate/MetaInput/FormatOptions";

type AdvancedProps = {
    store: DataSourcesStore;
};

const Advanced: React.FC<AdvancedProps> = observer(({ store }) => {
    const { advancedConfig, setAdvancedConfig, orgCampaignIds } = store;

    const [isOpen, setIsOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        setIsOpen(true);
    };
    const handleClose = () => {
        setAnchorEl(null);
        setIsOpen(false);
    };

    const {
        campaignIndicatorDefinitions,
        mediaSegments,
        dateFormatPatterns,
        languageIndicatorDefinitions,
    } = advancedConfig;

    const addCampaignDefinition = () => {
        setAdvancedConfig({
            ...advancedConfig,
            campaignIndicatorDefinitions: [
                ...(campaignIndicatorDefinitions || []),
                emptyCampaignIndicator(),
            ],
        });
    };

    const updateCampaignDefinition = (
        key: string,
        value: string | number | { id: string; value: string }[],
        id: string,
    ) => {
        setAdvancedConfig({
            ...advancedConfig,
            campaignIndicatorDefinitions: campaignIndicatorDefinitions?.map(
                (e) => {
                    if (e.id === id) {
                        return {
                            ...e,
                            [key]: value,
                        };
                    }
                    return e;
                },
            ),
        });
    };

    const removeCampaignDefinition = (id: string) => {
        setAdvancedConfig({
            ...advancedConfig,
            campaignIndicatorDefinitions: campaignIndicatorDefinitions?.filter(
                (e) => e.id !== id,
            ),
        });
    };

    const addLanguageDefinition = () => {
        setAdvancedConfig({
            ...advancedConfig,
            languageIndicatorDefinitions: [
                ...(languageIndicatorDefinitions || []),
                emptyLanguageIndicator(),
            ],
        });
    };

    const updateLanguageDefinition = (
        id: string,
        key: string,
        value: string | number | { id: string; value: string }[],
    ) => {
        setAdvancedConfig({
            ...advancedConfig,
            languageIndicatorDefinitions: languageIndicatorDefinitions?.map(
                (e) => {
                    if (e.id === id) {
                        return {
                            ...e,
                            [key]: value,
                        };
                    }
                    return e;
                },
            ),
        });
    };

    const removeLanguageDefinition = (id: string) => {
        setAdvancedConfig({
            ...advancedConfig,
            languageIndicatorDefinitions: languageIndicatorDefinitions?.filter(
                (e) => e.id !== id,
            ),
        });
    };

    const addMediaSegments = () => {
        setAdvancedConfig({
            ...advancedConfig,
            mediaSegments: {
                rootPath: "",
                start: "",
                endPrimary: "",
                endAlternate: "",
                agentName: "",
                agentId: "",
            },
        });
    };

    const updateMediaSegments = (key: string, value: string) => {
        if (!mediaSegments) return;

        setAdvancedConfig({
            ...advancedConfig,
            mediaSegments: {
                ...mediaSegments,
                [key]: value,
            },
        });
    };

    const removeMediaSegments = () => {
        setAdvancedConfig({
            ...advancedConfig,
            mediaSegments: undefined,
        });
    };

    const addDateFormatPattern = () => {
        setAdvancedConfig({
            ...advancedConfig,
            dateFormatPatterns: [
                ...dateFormatPatterns,
                { id: uuidv4(), pattern: "" },
            ],
        });
    };

    const updateDateFormatPattern = (
        key: string,
        value: string,
        id: string,
    ) => {
        setAdvancedConfig({
            ...advancedConfig,
            dateFormatPatterns: dateFormatPatterns.map((e) => {
                if (e.id === id) {
                    return {
                        ...e,
                        [key]: value,
                    };
                }
                return e;
            }),
        });
    };

    const removeDateFormatPattern = (id: string) => {
        setAdvancedConfig({
            ...advancedConfig,
            dateFormatPatterns: dateFormatPatterns.filter((e) => e.id !== id),
        });
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Section
                heading="Campaign Indicator Definitions"
                subHeading="Map fields to campaigns for effective reporting"
                shouldStartExpanded={!!campaignIndicatorDefinitions?.length}
            >
                {!!campaignIndicatorDefinitions?.length && (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                padding: theme.spacing(0, 8, 0, 2),
                            }}
                        >
                            <Typography
                                sx={{ fontWeight: 600, fontSize: 14, flex: 3 }}
                            >
                                Campaign
                            </Typography>
                            <Typography
                                sx={{ fontWeight: 600, fontSize: 14, flex: 6 }}
                            >
                                Field Path
                            </Typography>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: 14,
                                    flex: 3,
                                }}
                            >
                                Comparison Operator
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            {campaignIndicatorDefinitions.map((definition) => (
                                <CampaignDefinition
                                    key={definition.id}
                                    orgCampaignIds={orgCampaignIds}
                                    campaignIndicatorDefinition={definition}
                                    remove={() =>
                                        removeCampaignDefinition(definition.id)
                                    }
                                    update={(key, value) =>
                                        updateCampaignDefinition(
                                            key,
                                            value,
                                            definition.id,
                                        )
                                    }
                                />
                            ))}
                        </Box>
                    </>
                )}

                <AddButton onClick={addCampaignDefinition}>
                    Add Definition
                </AddButton>
            </Section>
            <Section
                heading="Language Indicator Definitions"
                subHeading="Specify values for accurate language identification"
                shouldStartExpanded={!!languageIndicatorDefinitions?.length}
            >
                {!!languageIndicatorDefinitions?.length && (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                padding: theme.spacing(0, 8, 0, 2),
                            }}
                        >
                            <Typography
                                sx={{ fontWeight: 600, fontSize: 14, flex: 3 }}
                            >
                                Local ID
                            </Typography>
                            <Typography
                                sx={{ fontWeight: 600, fontSize: 14, flex: 6 }}
                            >
                                Field Path
                            </Typography>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: 14,
                                    flex: 3,
                                }}
                            >
                                Comparison Operator
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            {languageIndicatorDefinitions.map((definition) => (
                                <LanguageIndicatorDefinition
                                    {...definition}
                                    key={definition.id}
                                    localeId={definition.localeId}
                                    remove={() =>
                                        removeLanguageDefinition(definition.id)
                                    }
                                    update={(key, value) =>
                                        updateLanguageDefinition(
                                            definition.id,
                                            key,
                                            value,
                                        )
                                    }
                                />
                            ))}
                        </Box>
                    </>
                )}

                <AddButton onClick={addLanguageDefinition}>
                    Add Definition
                </AddButton>
            </Section>
            <Section
                heading="Media Segments"
                subHeading="Configure start and end points of segments and map to agent fields"
                shouldStartExpanded={!!mediaSegments}
            >
                <MediaSegments
                    mediaSegments={mediaSegments}
                    updateMediaSegments={updateMediaSegments}
                    addMediaSegments={addMediaSegments}
                    removeMediaSegments={removeMediaSegments}
                />
            </Section>
            <Section
                heading="Date Format Patterns"
                subHeading="Specify and map date formats for consistent metadata parsing"
                shouldStartExpanded={!!dateFormatPatterns.length}
            >
                {!!dateFormatPatterns.length && (
                    <>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                                Date Format Pattern
                            </Typography>
                            <IconButton
                                onClick={handleClick}
                                aria-describedby="date-format-helper-text"
                                sx={{
                                    padding: 0,
                                    color: theme.palette.primary.main,
                                    svg: { width: 20, height: 20 },
                                }}
                            >
                                <InfoIcon />
                            </IconButton>
                            <Popover
                                id="date-format-helper-text"
                                open={isOpen}
                                anchorEl={anchorEl}
                                onClose={handleClose}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "left",
                                }}
                            >
                                <DateFormatHelperText />
                            </Popover>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            {dateFormatPatterns.map((pattern) => (
                                <DateFormatPattern
                                    key={pattern.id}
                                    datePattern={pattern}
                                    updateDateFormatPattern={(key, value) =>
                                        updateDateFormatPattern(
                                            key,
                                            value,
                                            pattern.id,
                                        )
                                    }
                                    removeDateFormatPattern={() =>
                                        removeDateFormatPattern(pattern.id)
                                    }
                                />
                            ))}
                        </Box>
                    </>
                )}
                <AddButton onClick={addDateFormatPattern}>
                    Add Pattern
                </AddButton>
            </Section>
        </Box>
    );
});

export default Advanced;
